// Generated by tutorials/record.cjs from tutorials/articles/h62-facility-setup.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h62 --text-only` (wording) or
// `node tutorials/record.cjs h62` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'facility-setup',
  catalogId: 'h62',
  title: 'Facility setup',
  summary: 'Facility Settings holds how your facility works: its name, house diet, meal names and times, the words it uses and what prints on tray tickets. Unit Rooms holds the rooms and beds in each unit. Here is how to find and change them, and what they change elsewhere.',
  audience: 'Administrators and dietary managers who set up the facility',
  area: 'setup',
  learn: [
    'Open Facility Settings and find a setting',
    'The tabs that matter most: diets, mealtimes, labels, tray tickets',
    'Add rooms to a unit and give them beds',
    'What units, rooms and beds change: the resident\'s room, Census, tray tickets and the tray line',
  ],
  keywords: [
    'facility',
    'facility settings',
    'settings',
    'setup',
    'facility setup',
    'house diet',
    'npo',
    'regular consistency',
    'mealtimes',
    'meal names',
    'meal times',
    'supper',
    'labels',
    'resident word',
    'patient',
    'time zone',
    'tray ticket settings',
    'unit',
    'units',
    'unit rooms',
    'rooms',
    'room',
    'beds',
    'bed',
    'add rooms',
    'quick add',
    'room number',
    'floor',
    'wing',
    'force room bed selection',
    'census by unit',
    'facilities',
  ],
  steps: [
    {
      id: 'open',
      part: 'Facility Settings',
      heading: 'Open Facility Settings',
      text: 'Select Setup in the menu, then Facility Settings. Your facility\'s name and ID are at the top.\n\nThe tabs are listed on the left, in groups: Facility, Display, Menus, Reports and Integration. It opens on General Info: the facility\'s name, abbreviation, address and phone, and Units.',
      media: {
        image: {
          src: '/help/facility-setup/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/facility-setup/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'Facility Settings for Maple Grove Care Center on General Info: name, abbreviation, address, phone, and the Units card with Enabled and Force Room/Bed Selection checked.',
      },
    },
    {
      id: 'units-switch',
      part: 'Facility Settings',
      heading: 'Units',
      text: 'With Units Enabled, each resident is in a unit (a floor and wing of the building) instead of having a floor and wing typed in.\n\nForce Room/Bed Selection makes staff pick the room and bed from the list you keep on Unit Rooms, so a resident cannot be put in a room that does not exist.',
      media: {
        image: {
          src: '/help/facility-setup/units-switch.webp',
          width: 1232,
          height: 218,
        },
        alt: 'The Units card on General Info: Enabled and Force Room/Bed Selection, both checked.',
      },
    },
    {
      id: 'search',
      part: 'Facility Settings',
      heading: 'Find a setting',
      text: 'There are many tabs. To find a setting, type a word in Search settings: only the tabs that have it stay in the list.\n\nHere, house finds Diet / Consistency. House Diet Type and Regular Consistency are your standard, unaltered diet and texture; the production tally combines them with similar orders. NPO Diet Type is the diet a resident is put on when their NPO special need is checked.',
      media: {
        image: {
          src: '/help/facility-setup/search.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/facility-setup/search.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.7,
        },
        alt: 'Search settings with house typed, the list down to Diet / Consistency, and its tab: House Diet Type Regular, Regular Consistency Regular and NPO Diet Type.',
      },
    },
    {
      id: 'mealtimes',
      part: 'Facility Settings',
      heading: 'Meal names and times',
      text: 'Mealtimes holds the names and times of your three meals and your nourishment passes. The names are used all through Dietech Online: on screens, menus, tray tickets and reports.\n\nHere the evening meal is renamed Supper.',
      media: {
        image: {
          src: '/help/facility-setup/mealtimes.webp',
          width: 1280,
          height: 906,
        },
        clip: {
          src: '/help/facility-setup/mealtimes.mp4',
          width: 1280,
          height: 906,
          durationSec: 4.7,
        },
        alt: 'The Mealtimes tab: Meal Names Morning Breakfast 7:30 AM, Mid Lunch 12:00 PM, Evening being changed to Supper at 5:00 PM, and the Nourishment Names below.',
      },
    },
    {
      id: 'labels',
      part: 'Facility Settings',
      heading: 'Your facility\'s words',
      text: 'Labels holds the words your facility uses. If you say Patient rather than Resident, or Suite rather than Room, change it here, and Dietech Online uses your word in the menu, on screens and in reports.',
      media: {
        image: {
          src: '/help/facility-setup/labels.webp',
          width: 1280,
          height: 906,
        },
        alt: 'The Labels tab on Names: Resident Labels (Resident, Residents, Resident\'s), Staff Labels (Prescriber Physician, Special Device) and Facility Labels.',
      },
    },
    {
      id: 'settings-tab',
      part: 'Facility Settings',
      heading: 'Settings and Tray Tickets',
      text: 'Settings holds how the facility works day to day, such as Production, the Time Zone your facility is in and, further down, the Room & Tray Line options. Advanced, at the top of the tab, has more.\n\nTray Tickets, under Reports, decides what prints on each tray ticket and how it looks. Printers are on the Print Agent tab: see Printers and printing problems.',
      media: {
        image: {
          src: '/help/facility-setup/settings-tab.webp',
          width: 1280,
          height: 906,
        },
        alt: 'The Settings tab on General: General Settings, the Enable options with Production checked, and Time Zone.',
      },
    },
    {
      id: 'save',
      part: 'Facility Settings',
      heading: 'Save',
      text: 'The bar at the bottom says Unsaved changes as soon as you change something, on any tab. Select Save to keep everything you changed, or Cancel to put it all back.\n\nA message at the top right says Settings saved successfully.',
      media: {
        image: {
          src: '/help/facility-setup/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/facility-setup/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.9,
        },
        alt: 'Facility Settings after saving: the message Settings saved successfully at the top right, and Save greyed out again.',
      },
    },
    {
      id: 'facilities',
      part: 'Facility Settings',
      heading: 'More than one facility',
      text: 'If you look after more than one facility, Setup, Facilities lists them. Select one and Open to change its settings. Census, beside Open, shows that facility\'s census history.',
      media: {
        image: {
          src: '/help/facility-setup/facilities.webp',
          width: 1280,
          height: 874,
        },
        alt: 'The Facilities list: Cedar Ridge Health Campus and Maple Grove Care Center with address, city, state and zip code, and Open and Census at the top.',
      },
    },
    {
      id: 'unit-rooms',
      part: 'Units, rooms and beds',
      heading: 'Open Unit Rooms',
      text: 'Select Setup, then Unit Rooms. Your units are on the left, with their floor (Fl) and wing (Wg).\n\nSelect a unit to see its rooms and the beds in each room. A room with (None) has no bed letter: one resident, no A or B.',
      media: {
        image: {
          src: '/help/facility-setup/unit-rooms.webp',
          width: 1280,
          height: 874,
        },
        clip: {
          src: '/help/facility-setup/unit-rooms.mp4',
          width: 1280,
          height: 874,
          durationSec: 4.3,
        },
        alt: 'Unit Rooms with Maple selected: rooms 101 to 111, each with Beds A, B, except 109 with A only.',
      },
    },
    {
      id: 'quick-add',
      part: 'Units, rooms and beds',
      heading: 'Add rooms',
      text: 'Select the unit, then Quick Add. Type the first room number in Start Room and the last in Stop Room.\n\nSkip leaves numbers out between rooms: 0 adds every number, 1 adds every other one (201, 203, 205...). Select Add Rooms. Rooms the unit already has are left as they are.',
      media: {
        image: {
          src: '/help/facility-setup/quick-add.webp',
          width: 708,
          height: 590,
        },
        clip: {
          src: '/help/facility-setup/quick-add.mp4',
          width: 708,
          height: 590,
          durationSec: 5.6,
        },
        alt: 'Quick Add Rooms to Aspen: Start Room 201, Stop Room 211 and Skip 1, with Cancel and Add Rooms.',
      },
    },
    {
      id: 'beds',
      part: 'Units, rooms and beds',
      heading: 'Give the rooms their beds',
      text: 'The new rooms start with no beds. Check the rooms (the box at the top of the list checks them all), then check the beds they have under Beds on the right, here A and B.\n\nBeds are saved as soon as you check them; there is no Save button.',
      media: {
        image: {
          src: '/help/facility-setup/beds.webp',
          width: 1280,
          height: 874,
        },
        clip: {
          src: '/help/facility-setup/beds.mp4',
          width: 1280,
          height: 874,
          durationSec: 6,
        },
        alt: 'Aspen\'s rooms 201 to 211 all checked, and A and B checked under Beds: every room now shows Beds A, B.',
      },
    },
    {
      id: 'room-number',
      part: 'Units, rooms and beds',
      heading: 'Change or delete a room',
      text: 'To change a room\'s number, double-click the room, type the new number and select Save.\n\nTo delete rooms, check them, then select More actions (the three dots) and Delete Room (Delete 3 Rooms when you checked three, and so on). Move any resident out of a room before you delete it.',
      media: {
        image: {
          src: '/help/facility-setup/room-number.webp',
          width: 708,
          height: 324,
        },
        alt: 'The Edit Room Number box for room 211, with the Room Number box and Cancel and Save.',
      },
    },
    {
      id: 'add-unit',
      part: 'Units, rooms and beds',
      heading: 'A new unit',
      text: 'Unit Rooms works with the units your facility has. To add a new unit, or to change a unit\'s floor, wing, colors or the order units come in at each meal, contact Dietech support. Once the unit is there, add its rooms and beds here as above.',
      textOnly: true,
    },
    {
      id: 'resident-room',
      part: 'Where rooms and units show up',
      heading: 'The resident\'s room and bed',
      text: 'On a resident\'s Primary Info, Room and Bed are lists of the rooms and beds you set up for the resident\'s unit, when Force Room/Bed Selection is checked. Pick another unit and the lists change to that unit\'s rooms.\n\nDietech Online also warns when two current residents are given the same room and bed.',
      media: {
        image: {
          src: '/help/facility-setup/resident-room.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/facility-setup/resident-room.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.8,
        },
        alt: 'Primary Info for Eleanor Whitfield: Unit Maple, and the Room list open with rooms 101, 103, 105, 107, 109 and 111.',
      },
    },
    {
      id: 'census',
      part: 'Where rooms and units show up',
      heading: 'Census by unit',
      text: 'Residents, Census counts your current residents in each unit, so the unit you give a resident decides where they are counted.',
      media: {
        image: {
          src: '/help/facility-setup/census.webp',
          width: 1280,
          height: 170,
        },
        alt: 'Census, Active Residents by Unit: Birch, Maple and Willow, each with its number of residents.',
      },
    },
    {
      id: 'tray',
      part: 'Where rooms and units show up',
      heading: 'Tray tickets and the tray line',
      text: 'The resident\'s room and bed print on their tray ticket. With Use Unit Color checked on the Tray Tickets tab, the room box is shaded with the color of the resident\'s unit.\n\nTray Line Setup uses your rooms too: its Auto setup puts the rooms in tray order and places each resident by their room and bed. Set up the rooms and beds first. See Tray line setup.',
      textOnly: true,
    },
  ],
  tips: [
    'Search settings, above the tab list, looks inside the tabs too: type a word such as supper or time zone and only the tabs that have it stay in the list.',
    'You can change several tabs and save once: Save keeps everything you changed on every tab.',
    'Number your rooms the way they are on the doors. Staff pick from this list when they admit a resident or move one.',
  ],
  notes: [
    'Changing facility settings needs Facility level 2 or higher. At level 1 the settings are view only.',
    'Printers and the print agent are on the Print Agent tab and under Setup, Printers. See Printers and printing problems.',
    'To add a new unit, or to change a unit\'s floor, wing, colors or meal order, contact Dietech support.',
    'Some tabs, such as Print Agent and Nutrition Compliance, have their own Save button for what they hold.',
  ],
  related: [
    'printers-and-printing',
    'tray-line-setup',
    'tray-tickets',
    'admit-resident',
    'roles-and-access',
    'settings-and-preferences',
  ],
  recorded: '2026-11-03',
};

export default article;
